/**
 * Helpers pour lire une erreur throw par `libs/shared/src/core/services/network/Api.ts`
 * et en produire un message métier utilisable côté UI (toast, bannière, table).
 *
 * Shape de l'erreur (Api.ts:65-77) :
 * ```
 * {
 *   type: 'server',
 *   nativeError,
 *   statusCode: response.status,
 *   config: { baseURL, url, params },
 *   ...response.data          // = { status, data: { errorMessage }, message }
 * }
 * ```
 */

export interface ApiErrorShape {
  statusCode?: number;
  status?: number;
  response?: { status?: number };
  data?: { errorMessage?: string };
  errorMessage?: string;
  message?: string;
}

export const getHttpStatus = (err: unknown): number | undefined => {
  if (err && typeof err === 'object') {
    const e = err as ApiErrorShape;
    return e.statusCode ?? e.response?.status ?? e.status;
  }
  return undefined;
};

export const getBackErrorMessage = (err: unknown): string | undefined => {
  if (err && typeof err === 'object') {
    const e = err as ApiErrorShape;
    return e.data?.errorMessage ?? e.errorMessage;
  }
  return undefined;
};

export const getBackMessage = (err: unknown): string | undefined => {
  if (err && typeof err === 'object') {
    const e = err as ApiErrorShape;
    return e.message;
  }
  return undefined;
};

/**
 * Produit un titre + description user-friendly pour un toast/bannière d'erreur.
 * Priorise les cas fonctionnels connus (403 INVALID_USER_TYPE, 401, réseau) avant
 * de retomber sur `message` back générique.
 */
export interface UserErrorMessage {
  title: string;
  description: string;
}

export const toUserErrorMessage = (err: unknown, fallbackTitle: string): UserErrorMessage => {
  if (!err) {
    return { title: fallbackTitle, description: 'Une erreur inconnue est survenue. Réessayez.' };
  }
  const status = getHttpStatus(err);
  const errorMessage = getBackErrorMessage(err);

  if (status === 403 && errorMessage === 'INVALID_USER_TYPE') {
    return {
      title: 'Permission manquante',
      description: "Vous n'avez pas les droits pour effectuer cette action. Contactez votre administrateur Allocab.",
    };
  }
  if (status === 403) {
    return {
      title: 'Action refusée',
      description: 'Cette opération est refusée pour votre compte. Contactez votre administrateur Allocab.',
    };
  }
  if (status === 401) {
    return {
      title: 'Session expirée',
      description: 'Votre session a expiré. Reconnectez-vous pour continuer.',
    };
  }
  if (status === 404) {
    return {
      title: 'Ressource introuvable',
      description: "La ressource demandée n'existe pas ou plus.",
    };
  }
  if (typeof status === 'number' && status >= 500) {
    return {
      title: 'Service indisponible',
      description: 'Le service Allocab est momentanément indisponible. Réessayez dans quelques instants.',
    };
  }
  if (!status) {
    return {
      title: 'Connexion perdue',
      description: 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.',
    };
  }
  return {
    title: fallbackTitle,
    description: getBackMessage(err) || 'Réessayez ou contactez votre administrateur Allocab.',
  };
};
